import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Redirect, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { sortBy } from 'lodash';
import { Position } from '@blueprintjs/core';
import {
  Box,
  Button,
  ConfirmDialog,
  Dialog,
  DialogFooter,
  DropdownMenu,
  EmptyState,
  Flex,
  FlexColumn,
  Icon,
  Menu,
  MenuItem,
  SegmentedControl,
  Spinner,
  Text
} from 'core/components';
import { ProductSkeleton } from 'core/components/Skeletons';
import { InputGroup } from 'core/form';
import { dateTimeFormatter, fromNow } from 'core/util/dateUtils';
import Page from 'app/components/page/Page';
import PageHeading from 'app/components/page/PageHeading';
import ChatSessionCollection from './models/ChatSessionCollection';
import KaizenSidebarLayout from './KaizenSidebarLayout';

// Others/Automated can hold thousands of chats; render them a page at a time
const PAGE_SIZE = 50;

const OWNER_OPTIONS = [
  { value: 'mine', label: 'My chats', icon: 'chat', emptyTitle: "You haven't started any chats yet" },
  { value: 'others', label: 'Others', icon: 'people', emptyTitle: 'No chats from other users' },
  { value: 'automated', label: 'Automated', icon: 'automatic-updates', emptyTitle: 'No automated chats' }
];

function getUserLabel(model) {
  return model.get('user.user_full_name') || model.get('user.user_email') || 'Unknown user';
}

function getOwnerType(model) {
  if (model.isAutomatedChat) {
    return 'automated';
  }
  return model.isUserChat ? 'mine' : 'others';
}

function matchesSearch(model, search) {
  if (!search) {
    return true;
  }

  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  const haystack = [model.chatSessionTitle, model.get('user.user_full_name'), model.get('user.user_email')]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return terms.every((term) => haystack.includes(term));
}

// Separate collection so pin/rename/delete here don't disturb the AI Advisor sidebar's selection state
class ChatSessionHistoryCollection extends ChatSessionCollection {
  get defaultSortState() {
    return { field: 'updated_at', direction: 'desc' };
  }
}

// lighter than the card border; rows are separated by hairlines only (no side borders)
const rowBorderColor = ({ theme }) => (theme.name === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(17, 20, 24, 0.07)');

const ChatRow = styled(Flex)`
  position: relative;
  align-items: center;
  gap: 12px;
  padding: 10px 8px 10px 14px;
  cursor: pointer;
  outline: none;
  transition: background-color 100ms ease;

  border-bottom: 1px solid ${rowBorderColor};

  &:first-child {
    border-top: 1px solid ${rowBorderColor};
  }

  /* same tint Blueprint uses for minimal button hover, so it works in both themes */
  &:hover,
  &:focus-visible,
  &:has(.bp4-popover-open) {
    background-color: rgba(143, 153, 168, 0.15);
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }

  /* like the sidebar list items: the slot shows the working/pinned status, swapped for the actions on hover */
  .row-actions {
    display: none;
  }

  &:hover,
  &:focus-within,
  &:has(.bp4-popover-open) {
    .row-actions {
      display: flex;
    }

    .row-status {
      display: none;
    }
  }
`;

const RowSlot = styled(Flex)`
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

// Keeps clicks on the row actions (including its menu, which bubbles through the portal) from opening the chat
const stopPropagation = (e) => e.stopPropagation();

const KaizenHistoryActions = observer(({ model, onTogglePin, onRename, onDelete }) => {
  const isPinned = model.get('pinned');

  return (
    <DropdownMenu
      position={Position.BOTTOM_RIGHT}
      content={
        <Menu>
          <MenuItem
            icon={isPinned ? 'unpin' : 'pin'}
            text={isPinned ? 'Unpin' : 'Pin'}
            onClick={() => onTogglePin(model)}
          />
          <MenuItem icon="edit" text="Rename" onClick={() => onRename(model)} />
          <MenuItem icon="trash" intent="danger" text="Delete" onClick={() => onDelete(model)} />
        </Menu>
      }
    >
      <Button icon="more" color="muted" minimal small ariaLabel="Chat actions" style={{ transform: 'rotate(90deg)' }} />
    </DropdownMenu>
  );
});

const KaizenHistoryRow = observer(({ model, owner, onOpen, ...actionHandlers }) => {
  const updatedAt = model.get('updated_at');
  const isPinned = model.isUserChat && model.get('pinned');
  // only the chat's owner can pin, rename, or delete it
  const hasActions = model.isUserChat;

  let statusIndicator = null;
  if (model.isAgentActive) {
    statusIndicator = <Spinner size={14} variant="hourglass" />;
  } else if (isPinned) {
    statusIndicator = <Icon icon="pin" color="muted" iconSize={14} title="Pinned" />;
  }

  const handleKeyDown = (e) => {
    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onOpen(model);
    }
  };

  return (
    <ChatRow role="link" tabIndex={0} onClick={() => onOpen(model)} onKeyDown={handleKeyDown}>
      <FlexColumn flex={1} minWidth={0} gap="2px">
        <Text fontWeight={500} ellipsis title={model.chatSessionTitle}>
          {model.chatSessionTitle}
        </Text>
        {owner !== 'mine' && (
          <Text muted small ellipsis>
            {getUserLabel(model)}
          </Text>
        )}
      </FlexColumn>

      {updatedAt && (
        <Text muted small flexShrink={0} title={dateTimeFormatter({ value: updatedAt })}>
          {fromNow(updatedAt)}
        </Text>
      )}

      {(owner === 'mine' || statusIndicator) && (
        <RowSlot>
          <Flex className="row-status">{statusIndicator}</Flex>
          {hasActions && (
            <Flex className="row-actions" onClick={stopPropagation}>
              <KaizenHistoryActions model={model} {...actionHandlers} />
            </Flex>
          )}
        </RowSlot>
      )}
    </ChatRow>
  );
});

const LoadingRows = () => (
  <Box mt={1}>
    {[70, 45, 60, 35, 55].map((width) => (
      <ChatRow key={width} py="14px">
        <ProductSkeleton width={`${width}%`} />
      </ChatRow>
    ))}
  </Box>
);

@inject('$auth', '$companySettings', '$kaizen')
@withRouter
@observer
class KaizenHistory extends Component {
  collection = new ChatSessionHistoryCollection();

  state = {
    owner: 'mine',
    search: '',
    limit: PAGE_SIZE,
    renameModel: null,
    renameTitle: '',
    renaming: false,
    deleteModel: null
  };

  componentDidMount() {
    this.collection.fetch();
  }

  // counts follow the search so you can see when matches are under another tab
  // hasFetched flips on when a fetch starts, so also wait out the first load before trusting an empty list
  get isInitialLoad() {
    const { collection } = this;
    return !collection.hasFetched || (collection.loading && collection.models.length === 0);
  }

  get ownerCounts() {
    const { search } = this.state;

    return this.collection.models.reduce((counts, model) => {
      if (matchesSearch(model, search)) {
        const owner = getOwnerType(model);
        counts[owner] = (counts[owner] || 0) + 1;
      }
      return counts;
    }, {});
  }

  get ownerOptions() {
    const counts = this.ownerCounts;

    return OWNER_OPTIONS.map(({ value, label }) => ({
      value,
      label: (
        <Flex alignItems="center" gap="6px">
          {label}
          {!this.isInitialLoad && (
            <Text muted small>
              {counts[value] || 0}
            </Text>
          )}
        </Flex>
      )
    }));
  }

  // pinned chats (mine only) stay on top, then most recently updated
  get visibleChats() {
    const { owner, search } = this.state;
    const chats = this.collection.models.filter(
      (model) => getOwnerType(model) === owner && matchesSearch(model, search)
    );
    return owner === 'mine' ? sortBy(chats, (model) => !model.get('pinned')) : chats;
  }

  // History has its own collection; mirror changes onto the AI Advisor sidebar's copy of the chat
  getSidebarModel(model) {
    const { $kaizen } = this.props;
    return $kaizen.chatSessionCollection.get(model.id);
  }

  handleOwnerChange = (owner) => {
    this.setState({ owner, limit: PAGE_SIZE });
  };

  handleSearchChange = (e) => {
    this.setState({ search: e.target.value, limit: PAGE_SIZE });
  };

  handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      this.handleClearSearch();
    }
  };

  handleClearSearch = () => {
    this.setState({ search: '', limit: PAGE_SIZE });
  };

  handleShowMore = () => {
    this.setState(({ limit }) => ({ limit: limit + PAGE_SIZE }));
  };

  handleOpenChat = (model) => {
    const { history, $kaizen } = this.props;
    history.push(`${$kaizen.baseUrl}/${model.id}`);
  };

  handleTogglePin = async (model) => {
    const pinned = !model.get('pinned');

    try {
      await model.save({ pinned });
      this.getSidebarModel(model)?.set({ pinned });
    } catch (error) {
      console.error('Error toggling pin status:', error);
    }
  };

  handleRenameClick = (model) => {
    this.setState({ renameModel: model, renameTitle: model.chatSessionTitle });
  };

  handleRenameTitleChange = (e) => {
    this.setState({ renameTitle: e.target.value });
  };

  handleRenameCancel = () => {
    this.setState({ renameModel: null, renameTitle: '', renaming: false });
  };

  handleRenameConfirm = async () => {
    const { renameModel, renameTitle } = this.state;

    this.setState({ renaming: true });

    try {
      await renameModel.save({ user_title: renameTitle }, { patch: true });
      this.getSidebarModel(renameModel)?.set({ user_title: renameTitle });
      this.handleRenameCancel();
    } catch (error) {
      console.error('Error renaming chat session:', error);
      this.setState({ renaming: false });
    }
  };

  handleDeleteClick = (model) => {
    this.setState({ deleteModel: model });
  };

  handleDeleteCancel = () => {
    this.setState({ deleteModel: null });
  };

  handleDeleteConfirm = async () => {
    const { $kaizen } = this.props;
    const { deleteModel } = this.state;

    try {
      await deleteModel.destroy();
      $kaizen.chatSessionCollection.remove(deleteModel.id);
      this.setState({ deleteModel: null });
    } catch (error) {
      console.error('Error deleting chat session:', error);
    }
  };

  handleRefresh = () => {
    this.collection.fetch({ force: true });
  };

  get emptyState() {
    const { owner, search } = this.state;

    if (search) {
      return (
        <EmptyState
          mt={6}
          icon="search"
          title="No matching chats"
          description={`Nothing in ${OWNER_OPTIONS.find((o) => o.value === owner).label.toLowerCase()} matches "${search}"`}
          action={<Button text="Clear search" onClick={this.handleClearSearch} />}
        />
      );
    }

    const { icon, emptyTitle } = OWNER_OPTIONS.find((o) => o.value === owner);
    return <EmptyState mt={6} icon={icon} title={emptyTitle} />;
  }

  get chatList() {
    const { owner, limit } = this.state;

    if (this.isInitialLoad) {
      return <LoadingRows />;
    }

    const chats = this.visibleChats;

    if (chats.length === 0) {
      return this.emptyState;
    }

    const remaining = chats.length - limit;

    return (
      <>
        <Box mt={1}>
          {chats.slice(0, limit).map((model) => (
            <KaizenHistoryRow
              key={model.id}
              model={model}
              owner={owner}
              onOpen={this.handleOpenChat}
              onTogglePin={this.handleTogglePin}
              onRename={this.handleRenameClick}
              onDelete={this.handleDeleteClick}
            />
          ))}
        </Box>
        {remaining > 0 && (
          <Flex justifyContent="center" mt={2}>
            <Button minimal text={`Show ${Math.min(remaining, PAGE_SIZE)} more`} onClick={this.handleShowMore} />
          </Flex>
        )}
      </>
    );
  }

  render() {
    const { $auth, $companySettings, $kaizen } = this.props;
    const { owner, search, renameModel, renameTitle, renaming, deleteModel } = this.state;

    if (!$companySettings.enableKentikAi || !$auth.hasPermission('kaizen', { overrideForSudo: false })) {
      return <Redirect to={$kaizen.baseUrl} />;
    }

    return (
      <Page title="History" pt={0} pb={0} px={0} overflow="hidden">
        <KaizenSidebarLayout onSelectJourney={this.handleOpenChat}>
          <FlexColumn flex="1 1 0%" minWidth={0} minHeight={0} overflow="hidden">
            {/* the header and toolbar stay put; only the list below scrolls */}
            <Box px={3} pt={2}>
              <Box width="100%" maxWidth={880} mx="auto">
                <Flex alignItems="center" justifyContent="space-between" gap={1}>
                  <PageHeading title="Chat History" mb={0} hideFavorite />
                  <Button
                    icon="refresh"
                    minimal
                    small
                    ariaLabel="Refresh chats"
                    title="Refresh"
                    loading={!this.isInitialLoad && this.collection.loading}
                    onClick={this.handleRefresh}
                  />
                </Flex>

                <Flex alignItems="center" gap={2} flexWrap="wrap" pt={2} pb="12px">
                  <Box flex="1 1 240px">
                    <InputGroup
                      type="search"
                      leftIcon="search"
                      placeholder="Search by title or user..."
                      value={search}
                      onChange={this.handleSearchChange}
                      onKeyDown={this.handleSearchKeyDown}
                      fill
                    />
                  </Box>
                  <SegmentedControl
                    inline
                    options={this.ownerOptions}
                    value={owner}
                    onChange={this.handleOwnerChange}
                    aria-label="Chat owner"
                  />
                </Flex>
              </Box>
            </Box>

            <Box flex={1} minHeight={0} overflow="auto" px={3} pb={4}>
              <Box width="100%" maxWidth={880} mx="auto">
                {this.chatList}
              </Box>
            </Box>
          </FlexColumn>
        </KaizenSidebarLayout>

        <Dialog
          title="Rename Conversation"
          isOpen={!!renameModel}
          onClose={this.handleRenameCancel}
          style={{ width: 400 }}
        >
          <div style={{ padding: '16px' }}>
            <InputGroup
              value={renameTitle}
              onChange={this.handleRenameTitleChange}
              placeholder="Enter new title..."
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button text="Cancel" onClick={this.handleRenameCancel} mr={1} />
            <Button
              text="Rename"
              intent="primary"
              onClick={this.handleRenameConfirm}
              disabled={!renameTitle.trim() || renaming}
              loading={renaming}
            />
          </DialogFooter>
        </Dialog>

        <ConfirmDialog
          title="Delete Conversation"
          model={deleteModel}
          text={`Are you sure you want to delete ${deleteModel?.chatSessionTitle}?`}
          isOpen={!!deleteModel}
          onConfirm={this.handleDeleteConfirm}
          onCancel={this.handleDeleteCancel}
          confirmButtonText="Delete"
          confirmButtonIntent="danger"
        />
      </Page>
    );
  }
}

export default KaizenHistory;
